import {
  getBackendErrorCode,
  getBackendErrorEvidence
} from '../../server/helper'

export const DEFAULT_404_ERROR_SHORTCODE = '#defaultMessage'

export const ERROR_HANDLER = {
  OPEN_SIGN_IN_WINDOW: 'OPEN_SIGN_IN_WINDOW'
}
/*
 * names of HTTP response status codes/names were taken from this link
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 * */

export const HTTP_STATUS_CODE = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  GONE: 410,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
}

export const ERROR_MESSAGE = {
  DEFAULT:
    "It looks like we're having trouble connecting to the server. Please check your internet connection and try again.",
  VERIFICATION_CODE:
    'The verification code you entered is incorrect. Please double-check and try again.',
  CATEGORY_VOTE_ALREADY_EXISTS:
    "It looks like you've already submitted your vote on another tab or device. Only one vote is allowed per person. If you believe this is a mistake, please refresh the page or contact support for assistance.",
  EXTRA_CATEGORY_VOTE_NOMINEES: 'Too many nominees were selected.',
  MISSING_CATEGORY_VOTE_NOMINEES:
    'Please select nominees for this category before submitting your vote.',
  INVALID_CATEGORY_VOTE_NOMINEES: 'Some of the selected nominees are not valid.'
}

export const ERROR_TITLE = {
  DEFAULT: 'Error - Network Issue',
  VERIFICATION_CODE: 'Verification Code',
  CATEGORY_VOTE_ALREADY_EXISTS: 'Vote Already Submitted'
}

/**
 * In case of redirecting user to error page we always show 404 status
 * and message "Page not found" (requested by client)
 * Commented message and status are real mapping (could be used later)
 */
const DEFAULT_INFO_SETTINGS = {
  title: ERROR_TITLE.DEFAULT,
  message: ERROR_MESSAGE.DEFAULT,
  status: HTTP_STATUS_CODE.NOT_FOUND
}

const HTTP_STATUS_INFO_SETTINGS = {
  [HTTP_STATUS_CODE.BAD_REQUEST]: {
    // title: 'Service unavailable',
    // message: ERROR_MESSAGE.DEFAULT_SERVER_ERROR,
    // status: HTTP_STATUS_CODE.SERVICE_UNAVAILABLE
    ...DEFAULT_INFO_SETTINGS
  },
  [HTTP_STATUS_CODE.FORBIDDEN]: {
    // title: 'Forbidden',
    // message: 'You do not have access to this page',
    // status: HTTP_STATUS_CODE.FORBIDDEN
    ...DEFAULT_INFO_SETTINGS
  },
  [HTTP_STATUS_CODE.NOT_FOUND]: {
    // title: ERROR_MESSAGE.PAGE_NOT_FOUND,
    // message:
    //   "404 Error. It could be an outdated link or just a small technical glitch. Try the URL again, or find what you're looking for on our <a href='/'>Homepage</a>.",
    // status: HTTP_STATUS_CODE.NOT_FOUND
    ...DEFAULT_INFO_SETTINGS
  },
  [HTTP_STATUS_CODE.METHOD_NOT_ALLOWED]: {
    // title: ERROR_MESSAGE.PAGE_NOT_FOUND,
    // message:
    //   "405 Error. It could be an outdated link or just a small technical glitch. Try the URL again, or find what you're looking for on our <a href='/'>Homepage</a>.",
    // status: HTTP_STATUS_CODE.METHOD_NOT_ALLOWED
    ...DEFAULT_INFO_SETTINGS
  },
  [HTTP_STATUS_CODE.GONE]: {
    title: 'Page deleted or gone',
    message:
      'The page you are looking for may have been deleted, please visit our <a href="/">homepage</a> for the latest info.',
    status: HTTP_STATUS_CODE.GONE
  },
  [HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR]: {
    // title: 'Internal Server Error',
    // message: ERROR_MESSAGE.DEFAULT_SERVER_ERROR,
    // status: HTTP_STATUS_CODE.SERVICE_UNAVAILABLE
    ...DEFAULT_INFO_SETTINGS
  },
  [HTTP_STATUS_CODE.SERVICE_UNAVAILABLE]: {
    // title: 'Internal Server Error',
    // message: ERROR_MESSAGE.DEFAULT_SERVER_ERROR,
    // status: HTTP_STATUS_CODE.SERVICE_UNAVAILABLE
    ...DEFAULT_INFO_SETTINGS
  }
}

const DEFAULT_ERROR_SETTINGS = {
  status: 404,
  showErrorPage: false,
  showMessage: true,
  skipLogging: false
}

export const ERROR_CODE = {
  INTERNAL_SERVER_ERROR: 'awards.api.00000',
  INVALID_USER_VERIFICATION_CODE: 'awards.domain.00001',
  CATEGORY_VOTE_ALREADY_EXISTS: 'awards.domain.00002',
  EXTRA_CATEGORY_VOTE_NOMINEES: 'awards.domain.00003',
  MISSING_CATEGORY_VOTE_NOMINEES: 'awards.domain.00004',
  INVALID_CATEGORY_VOTE_NOMINEES: 'awards.domain.00005'
}

export const ERROR_CODE_INFO_SETTINGS = {
  [ERROR_CODE.INTERNAL_SERVER_ERROR]: {
    ...DEFAULT_ERROR_SETTINGS,
    message: ERROR_MESSAGE.DEFAULT,
    title: ERROR_TITLE.DEFAULT,
    code: ERROR_CODE.INTERNAL_SERVER_ERROR
  },
  [ERROR_CODE.INVALID_USER_VERIFICATION_CODE]: {
    ...DEFAULT_ERROR_SETTINGS,
    code: ERROR_CODE.INVALID_USER_VERIFICATION_CODE,
    title: ERROR_TITLE.VERIFICATION_CODE,
    message: ERROR_MESSAGE.VERIFICATION_CODE
  },
  [ERROR_CODE.CATEGORY_VOTE_ALREADY_EXISTS]: {
    ...DEFAULT_ERROR_SETTINGS,
    code: ERROR_CODE.CATEGORY_VOTE_ALREADY_EXISTS,
    title: ERROR_TITLE.CATEGORY_VOTE_ALREADY_EXISTS,
    message: ERROR_MESSAGE.CATEGORY_VOTE_ALREADY_EXISTS
  },
  [ERROR_CODE.EXTRA_CATEGORY_VOTE_NOMINEES]: {
    ...DEFAULT_ERROR_SETTINGS,
    code: ERROR_CODE.EXTRA_CATEGORY_VOTE_NOMINEES,
    message: ERROR_MESSAGE.EXTRA_CATEGORY_VOTE_NOMINEES
  },
  [ERROR_CODE.MISSING_CATEGORY_VOTE_NOMINEES]: {
    ...DEFAULT_ERROR_SETTINGS,
    code: ERROR_CODE.MISSING_CATEGORY_VOTE_NOMINEES,
    message: ERROR_MESSAGE.MISSING_CATEGORY_VOTE_NOMINEES
  },
  [ERROR_CODE.INVALID_CATEGORY_VOTE_NOMINEES]: {
    ...DEFAULT_ERROR_SETTINGS,
    code: ERROR_CODE.INVALID_CATEGORY_VOTE_NOMINEES,
    message: ERROR_MESSAGE.INVALID_CATEGORY_VOTE_NOMINEES
  }
}

export function getHttpStatusInfo(status) {
  return (
    HTTP_STATUS_INFO_SETTINGS[status] ||
    HTTP_STATUS_INFO_SETTINGS[HTTP_STATUS_CODE.BAD_REQUEST]
  )
}

export function getErrorSettingsByBackendError(error) {
  const code = getBackendErrorCode(error)
  const evidence = getBackendErrorEvidence(error)

  const errorInfo =
    ERROR_CODE_INFO_SETTINGS[code] ||
    ERROR_CODE_INFO_SETTINGS[ERROR_CODE.INTERNAL_SERVER_ERROR]

  const message =
    'message' in errorInfo ? errorInfo.message : errorInfo.getMessage(evidence)

  const errorSettings = {
    ...errorInfo,
    message
  }

  return errorSettings
}
